const PrivacyContent = () => {
  return (
    <div className="text-muted-foreground space-y-4">
      <h3 className="font-heading text-foreground text-base font-bold">1. Overview</h3>
      <p className="leading-relaxed">
        qwip is operated by QWIP LTD, registered in England and Wales. Registered address: 128 City Road, London, EC1V 2NX.
      </p>
      <p className="leading-relaxed">
        This policy explains how we collect, use, and protect your personal data when you use the qwip website, mobile app, and pod booking service.
      </p>
      <p className="leading-relaxed">
        For any questions about this policy or your personal data, contact us at <a href="mailto:hello@qwip.co.uk" className="text-accent hover:underline">hello@qwip.co.uk</a>.
      </p>

      <h3 className="font-heading text-foreground text-base font-bold">2. Data We Collect</h3>

      <p className="leading-relaxed font-semibold text-foreground">Account and booking information</p>
      <ul className="list-disc list-inside space-y-1 leading-relaxed">
        <li>Name and email address</li>
        <li>Phone number (if provided)</li>
        <li>Booking history and session duration</li>
      </ul>

      <p className="leading-relaxed font-semibold text-foreground">Payment information</p>
      <p className="leading-relaxed">
        Payment card details are collected and processed securely by our payment provider, Stripe. We do not store your full card details on our systems.
      </p>

      <p className="leading-relaxed font-semibold text-foreground">Location data</p>
      <p className="leading-relaxed">
        When you use the qwip app, we request access to your device's location services to show you nearby spots and provide directions. Location data is only accessed while you are actively using the app. We do not track your location in the background. You can disable location services at any time through your device settings, though this may limit the app's functionality.
      </p>

      <p className="leading-relaxed font-semibold text-foreground">User-provided location preferences</p>
      <p className="leading-relaxed">
        You may choose to provide your approximate work and home areas (for example, "South East London" or "West London") to help us show you relevant spots. This information is provided voluntarily by you, is not precise or postcode-level, and is not derived from GPS or device tracking. It is stored in our database (Supabase) and analytics platform (PostHog).
      </p>

      <p className="leading-relaxed font-semibold text-foreground">Spot activity and usage data</p>
      <p className="leading-relaxed">
        When you use the app, we collect information about how you interact with it, including:
      </p>
      <ul className="list-disc list-inside space-y-1 leading-relaxed">
        <li>When you view a spot</li>
        <li>When you request directions to a spot</li>
        <li>When you save a spot to a collection</li>
        <li>When you share a collection</li>
      </ul>
      <p className="leading-relaxed">
        This data is collected through our analytics provider, PostHog, and is used to improve the app and understand which spots and features are most useful.
      </p>

      <p className="leading-relaxed font-semibold text-foreground">Technical information</p>
      <ul className="list-disc list-inside space-y-1 leading-relaxed">
        <li>IP address</li>
        <li>Device type and operating system</li>
        <li>Browser type (if using the web app)</li>
        <li>App version</li>
        <li>Date and time of access</li>
      </ul>

      <h3 className="font-heading text-foreground text-base font-bold">3. How We Use Your Data</h3>
      <p className="leading-relaxed">We use your data to:</p>
      <ul className="list-disc list-inside space-y-1 leading-relaxed">
        <li>Provide the qwip app and show you relevant spots based on your location</li>
        <li>Provide access to pods and process your bookings</li>
        <li>Calculate and charge for your sessions</li>
        <li>Send transactional emails (booking confirmations, session links, receipts)</li>
        <li>Send marketing communications (where you have opted in)</li>
        <li>Respond to queries and support requests</li>
        <li>Analyse how the app is used and improve our service</li>
        <li>Personalise your experience based on your provided location preferences</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h3 className="font-heading text-foreground text-base font-bold">4. Legal Basis</h3>
      <p className="leading-relaxed">We process your data under the following legal bases:</p>
      <ul className="list-disc list-inside space-y-1 leading-relaxed">
        <li><strong>Contract:</strong> To provide our service, process your bookings, and deliver the app's core functionality</li>
        <li><strong>Legitimate interests:</strong> To improve our service, analyse app usage, ensure security, and prevent fraud</li>
        <li><strong>Consent:</strong> To access your device location, and to send you marketing communications</li>
        <li><strong>Legal obligation:</strong> To comply with applicable laws and regulations</li>
      </ul>

      <h3 className="font-heading text-foreground text-base font-bold">5. Data Sharing</h3>
      <p className="leading-relaxed">
        We share your data with the following third-party service providers who help us operate qwip:
      </p>
      <ul className="list-disc list-inside space-y-1 leading-relaxed">
        <li><strong>Stripe</strong> (payment processing) — USA</li>
        <li><strong>Resend</strong> (transactional and marketing emails) — USA</li>
        <li><strong>Supabase</strong> (database and hosting) — EU</li>
        <li><strong>PostHog</strong> (product analytics) — EU</li>
      </ul>
      <p className="leading-relaxed">
        These providers process data on our behalf under data processing agreements and are required to protect your data in accordance with applicable law. Where data is transferred outside the UK (for example, to US-based providers), appropriate safeguards are in place including Standard Contractual Clauses. Where data is transferred to the EU, this is covered by the UK's adequacy decision for the EEA.
      </p>
      <p className="leading-relaxed">
        We do not sell your personal data to third parties.
      </p>

      <p className="leading-relaxed font-semibold text-foreground">Community features</p>
      <p className="leading-relaxed">
        The qwip app allows you to save spots to collections and share those collections via a link. When you share a collection, the contents of that collection (spot names and locations) are visible to anyone with the link. Your name and personal details are not included in shared collection links unless you choose to include them.
      </p>

      <h3 className="font-heading text-foreground text-base font-bold">6. Data Retention</h3>
      <p className="leading-relaxed">
        We retain your personal data for as long as you have an active account with us, and for a reasonable period thereafter to comply with legal obligations, resolve disputes, and enforce our agreements.
      </p>
      <p className="leading-relaxed">
        Booking and payment records are retained for 7 years in accordance with UK financial record-keeping requirements.
      </p>
      <p className="leading-relaxed">
        Analytics data collected through PostHog is retained for up to 24 months and is used in aggregate to improve the service.
      </p>
      <p className="leading-relaxed">
        If you delete your account, we will delete or anonymise your personal data within 30 days, except where retention is required by law.
      </p>

      <h3 className="font-heading text-foreground text-base font-bold">7. Your Rights</h3>
      <p className="leading-relaxed">Under UK GDPR, you have the right to:</p>
      <ul className="list-disc list-inside space-y-1 leading-relaxed">
        <li><strong>Access</strong> your personal data</li>
        <li><strong>Rectify</strong> inaccurate or incomplete data</li>
        <li><strong>Erase</strong> your data (in certain circumstances)</li>
        <li><strong>Restrict</strong> processing of your data</li>
        <li><strong>Object</strong> to processing based on legitimate interests</li>
        <li><strong>Data portability</strong> — receive your data in a structured, commonly used format</li>
        <li><strong>Withdraw consent</strong> at any time (where processing is based on consent)</li>
      </ul>
      <p className="leading-relaxed">
        To exercise any of these rights, contact us at <a href="mailto:hello@qwip.co.uk" className="text-accent hover:underline">hello@qwip.co.uk</a>. We will respond within one month.
      </p>

      <h3 className="font-heading text-foreground text-base font-bold">8. Marketing Communications</h3>
      <p className="leading-relaxed">
        Where you have opted in, we may send you marketing emails about qwip services, updates, and offers. You can unsubscribe at any time by clicking the unsubscribe link in any email or by contacting us at <a href="mailto:hello@qwip.co.uk" className="text-accent hover:underline">hello@qwip.co.uk</a>.
      </p>

      <h3 className="font-heading text-foreground text-base font-bold">9. Cookies</h3>
      <p className="leading-relaxed">
        Our website and booking platform use essential cookies required for the service to function. We do not currently use advertising cookies.
      </p>
      <p className="leading-relaxed">
        The qwip app uses PostHog for product analytics, which may use local storage or identifiers on your device to understand how you use the app. This data is used solely to improve qwip and is not shared with advertisers.
      </p>

      <h3 className="font-heading text-foreground text-base font-bold">10. Security</h3>
      <p className="leading-relaxed">
        We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, or misuse. Payment data is handled securely by Stripe in accordance with PCI-DSS standards.
      </p>

      <h3 className="font-heading text-foreground text-base font-bold">11. CCTV</h3>
      <p className="leading-relaxed">
        qwip pods are located in venues that may operate their own CCTV systems. The interior of the pod is not recorded by qwip. For information about venue CCTV, please refer to the relevant venue operator's privacy policy.
      </p>

      <h3 className="font-heading text-foreground text-base font-bold">12. Children's Privacy</h3>
      <p className="leading-relaxed">
        The qwip app and service are not directed at children under the age of 16. We do not knowingly collect personal data from children under 16. If you believe a child has provided us with personal data, please contact us at <a href="mailto:hello@qwip.co.uk" className="text-accent hover:underline">hello@qwip.co.uk</a> and we will delete it.
      </p>

      <h3 className="font-heading text-foreground text-base font-bold">13. Changes to This Policy</h3>
      <p className="leading-relaxed">
        We may update this policy from time to time. We will notify you of significant changes by email or by notice in the app or on our website. Continued use of qwip following any changes constitutes acceptance of the revised policy.
      </p>

      <h3 className="font-heading text-foreground text-base font-bold">14. Complaints</h3>
      <p className="leading-relaxed">
        If you are not satisfied with how we handle your data, you have the right to lodge a complaint with the Information Commissioner's Office (ICO):
      </p>
      <p className="leading-relaxed">
        Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">ico.org.uk</a><br />
        Phone: 0303 123 1113
      </p>

      <h3 className="font-heading text-foreground text-base font-bold">15. Contact</h3>
      <p className="leading-relaxed">
        For any questions about this policy or to exercise your rights:
      </p>
      <p className="leading-relaxed">
        <strong>Email:</strong> <a href="mailto:hello@qwip.co.uk" className="text-accent hover:underline">hello@qwip.co.uk</a><br />
        <strong>Address:</strong> QWIP LTD, 128 City Road, London, EC1V 2NX
      </p>
    </div>
  );
};

export default PrivacyContent;
